import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { jobsApi, uploadApi, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Search, Loader2, Heart, MessageCircle, Send, Trash2, ImagePlus, X,
  Briefcase, UserSearch, GraduationCap, BookOpen, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';

const TAGS = [
  { id: 'all', label: 'All Posts', icon: MessageSquare },
  { id: 'hiring', label: 'Hiring', icon: Briefcase },
  { id: 'looking_for_work', label: 'Looking for Work', icon: UserSearch },
  { id: 'career_advice', label: 'Career Advice', icon: GraduationCap },
  { id: 'training', label: 'Training', icon: BookOpen },
  { id: 'general', label: 'General', icon: MessageSquare },
];

const TAG_COLORS = {
  hiring: 'bg-green-100 text-green-700 border-green-200',
  looking_for_work: 'bg-blue-100 text-blue-700 border-blue-200',
  career_advice: 'bg-purple-100 text-purple-700 border-purple-200',
  training: 'bg-orange-100 text-orange-700 border-orange-200',
  general: 'bg-gray-100 text-gray-600 border-gray-200',
};

const TAG_LABELS = {
  hiring: 'Hiring',
  looking_for_work: 'Looking for Work',
  career_advice: 'Career Advice',
  training: 'Training',
  general: 'General',
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

function PostComments({ postId, user, isAuthenticated }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadComments();
  }, [postId]);

  const loadComments = async () => {
    try {
      const { data } = await jobsApi.getComments(postId);
      setComments(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;
    setSending(true);
    try {
      const { data } = await jobsApi.createComment(postId, {
        content: newComment.trim(),
        parent_comment_id: replyTo?.id || null
      });
      setComments(prev => [...prev, data]);
      setNewComment('');
      setReplyTo(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const handleLikeComment = async (commentId) => {
    if (!isAuthenticated) return;
    try {
      const { data } = await jobsApi.toggleCommentLike(commentId);
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, like_count: data.like_count, liked: data.liked } : c
      ));
    } catch { /* ignore */ }
  };

  // Thread comments
  const topLevel = comments.filter(c => !c.parent_comment_id);
  const replies = comments.filter(c => c.parent_comment_id);

  if (loading) return <div className="py-3 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-3" data-testid="comments-section">
      {topLevel.map(comment => (
        <div key={comment.id}>
          <div className="flex gap-2.5" data-testid={`comment-${comment.id}`}>
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
              {(comment.user_name || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="bg-muted/60 rounded-xl px-3 py-2">
                <p className="text-xs font-semibold">{comment.user_name}</p>
                <p className="text-[13px] leading-relaxed">{comment.content}</p>
              </div>
              <div className="flex items-center gap-3 mt-1 pl-1">
                <span className="text-[11px] text-muted-foreground">{timeAgo(comment.created_at)}</span>
                <button
                  onClick={() => handleLikeComment(comment.id)}
                  className={`text-[11px] font-semibold ${comment.liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`}
                >
                  Like{comment.like_count > 0 && ` (${comment.like_count})`}
                </button>
                {isAuthenticated && (
                  <button
                    onClick={() => setReplyTo(comment)}
                    className="text-[11px] font-semibold text-muted-foreground hover:text-primary"
                  >
                    Reply
                  </button>
                )}
              </div>
              {/* Replies */}
              {replies.filter(r => r.parent_comment_id === comment.id).map(reply => (
                <div key={reply.id} className="flex gap-2 mt-2 ml-2" data-testid={`reply-${reply.id}`}>
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-primary">
                    {(reply.user_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="bg-muted/40 rounded-xl px-3 py-1.5">
                      <p className="text-xs font-semibold">{reply.user_name}</p>
                      <p className="text-[13px] leading-relaxed">{reply.content}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 pl-1">
                      <span className="text-[11px] text-muted-foreground">{timeAgo(reply.created_at)}</span>
                      <button
                        onClick={() => handleLikeComment(reply.id)}
                        className={`text-[11px] font-semibold ${reply.liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'}`}
                      >
                        Like{reply.like_count > 0 && ` (${reply.like_count})`}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Comment Input */}
      {isAuthenticated && (
        <form onSubmit={handleSubmit} className="flex gap-2 items-start pt-1">
          <div className="flex-1">
            {replyTo && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                Replying to <span className="font-semibold">{replyTo.user_name}</span>
                <button type="button" onClick={() => setReplyTo(null)} className="ml-1"><X className="h-3 w-3" /></button>
              </div>
            )}
            <Input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className="h-9 text-[13px]"
              disabled={sending}
              data-testid="comment-input"
            />
          </div>
          <Button type="submit" size="icon" className="h-9 w-9 flex-shrink-0" disabled={!newComment.trim() || sending} data-testid="comment-submit">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        </form>
      )}
    </div>
  );
}

function PostCard({ post, user, isAuthenticated, onDelete, onLike }) {
  const [showComments, setShowComments] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isLong = post.content.length > 300;
  const isOwner = user && post.user_id === user.id;

  return (
    <Card className="overflow-hidden" data-testid={`post-${post.id}`}>
      <CardContent className="p-0">
        {/* Post Header */}
        <div className="flex items-start gap-3 p-4 pb-0">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-bold text-primary">
            {(post.user_name || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{post.user_name}</span>
              <Badge className={`text-[10px] px-1.5 py-0 border ${TAG_COLORS[post.tag] || TAG_COLORS.general}`}>
                {TAG_LABELS[post.tag] || 'General'}
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground">{timeAgo(post.created_at)}</p>
          </div>
          {isOwner && (
            <button
              onClick={() => onDelete(post.id)}
              className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
              data-testid={`delete-post-${post.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Post Content */}
        <div className="px-4 pt-3 pb-2">
          <p className={`text-[15px] leading-[1.6] whitespace-pre-wrap ${isLong && !expanded ? 'line-clamp-5' : ''}`}>
            {post.content}
          </p>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs font-semibold text-primary hover:text-primary/70 mt-1 flex items-center gap-0.5"
            >
              {expanded ? <><ChevronUp className="h-3 w-3" /> Show less</> : <><ChevronDown className="h-3 w-3" /> Read more</>}
            </button>
          )}
        </div>

        {/* Post Images */}
        {post.images && post.images.length > 0 && (
          <div className={`px-4 pb-2 ${post.images.length === 1 ? '' : 'grid grid-cols-2 gap-1.5'}`}>
            {post.images.map((img, i) => (
              <img
                key={i}
                src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`}
                alt=""
                className="w-full rounded-lg object-cover max-h-80"
              />
            ))}
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex items-center border-t border-border mx-4 py-2 gap-1">
          <button
            onClick={() => onLike(post.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              post.liked ? 'text-red-500 bg-red-50' : 'text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`like-post-${post.id}`}
          >
            <Heart className={`h-4 w-4 ${post.liked ? 'fill-red-500' : ''}`} />
            {post.like_count > 0 && post.like_count}
          </button>
          <button
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
            data-testid={`toggle-comments-${post.id}`}
          >
            <MessageCircle className="h-4 w-4" />
            {post.comment_count > 0 && post.comment_count}
          </button>
        </div>

        {/* Comments */}
        {showComments && (
          <div className="px-4 pb-4 border-t border-border pt-3">
            <PostComments postId={post.id} user={user} isAuthenticated={isAuthenticated} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function JobsCommunityPage() {
  const { user, isAuthenticated } = useAuth();
  const fileInputRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newTag, setNewTag] = useState('general');
  const [newImages, setNewImages] = useState([]);
  const [posting, setPosting] = useState(false);

  useEffect(() => { loadPosts(); }, [activeTag]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTag !== 'all') params.tag = activeTag;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const { data } = await jobsApi.getPosts(params);
      setPosts(data);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPosts();
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 4 - newImages.length;
    files.slice(0, remaining).forEach(file => {
      if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB per image'); return; }
      setNewImages(prev => [...prev, { file, preview: URL.createObjectURL(file), id: Date.now() + Math.random() }]);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreatePost = async () => {
    if (!newContent.trim()) { toast.error('Write something first'); return; }
    setPosting(true);
    try {
      // Upload images first
      const imageUrls = [];
      for (const img of newImages) {
        try {
          const res = await uploadApi.uploadImage(img.file);
          imageUrls.push(res.data.url);
        } catch { /* skip failed uploads */ }
      }

      const { data } = await jobsApi.createPost({
        content: newContent.trim(),
        tag: newTag,
        images: imageUrls
      });
      setPosts(prev => [data, ...prev]);
      setNewContent('');
      setNewTag('general');
      setNewImages([]);
      setShowCreate(false);
      toast.success('Post published!');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPosting(false); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await jobsApi.deletePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleLike = async (postId) => {
    if (!isAuthenticated) { toast.error('Please login to like posts'); return; }
    try {
      const { data } = await jobsApi.toggleLike(postId);
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, like_count: data.like_count, liked: data.liked } : p
      ));
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen pb-12" data-testid="jobs-community-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Jobs Community
          </h1>
          <p className="text-[15px] text-muted-foreground mt-1">
            Discuss jobs, share opportunities, and connect with the community
          </p>
        </div>

        {/* Create Post Prompt */}
        {isAuthenticated && !showCreate && (
          <Card className="mb-5 cursor-pointer hover:ring-1 hover:ring-primary/20 transition-all" onClick={() => setShowCreate(true)} data-testid="create-post-trigger">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-bold text-primary">
                {(user?.name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 h-10 bg-muted/60 rounded-full flex items-center px-4 text-sm text-muted-foreground">
                Share a job, opportunity, or career tip...
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Post Form */}
        {isAuthenticated && showCreate && (
          <Card className="mb-5 ring-1 ring-primary/20" data-testid="create-post-form">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-bold text-primary">
                  {(user?.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <div className="flex gap-1.5 flex-wrap mt-1">
                    {TAGS.filter(t => t.id !== 'all').map(t => (
                      <button
                        key={t.id}
                        onClick={() => setNewTag(t.id)}
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                          newTag === t.id ? TAG_COLORS[t.id] : 'border-border text-muted-foreground hover:border-primary/30'
                        }`}
                        data-testid={`tag-select-${t.id}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="What's on your mind? Share a job, ask for advice..."
                rows={4}
                className="resize-none text-[15px]"
                autoFocus
                data-testid="post-content-input"
              />

              {/* Image Previews */}
              {newImages.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {newImages.map((img, i) => (
                    <div key={img.id} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <img src={img.preview} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setNewImages(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-0.5 right-0.5 p-0.5 bg-black/60 rounded-full"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between pt-1">
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                    disabled={newImages.length >= 4}
                    data-testid="add-image-btn"
                  >
                    <ImagePlus className="h-5 w-5" />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setNewContent(''); setNewImages([]); }}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleCreatePost} disabled={posting || !newContent.trim()} className="gap-1.5" data-testid="publish-post-btn">
                    {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Post
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              className="pl-9 h-10"
              data-testid="search-posts-input"
            />
          </div>
        </form>

        {/* Tag Filters */}
        <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-none -mx-4 px-4" data-testid="tag-filters">
          {TAGS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTag(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold whitespace-nowrap border transition-all ${
                  activeTag === t.id
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-primary/30'
                }`}
                data-testid={`filter-${t.id}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Posts Feed */}
        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center">
            <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="font-semibold text-gray-700">No posts yet</p>
            <p className="text-sm text-muted-foreground mt-1">Be the first to share a job or opportunity!</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="posts-feed">
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                user={user}
                isAuthenticated={isAuthenticated}
                onDelete={handleDelete}
                onLike={handleLike}
              />
            ))}
          </div>
        )}

        {/* Login Prompt */}
        {!isAuthenticated && (
          <Card className="mt-6 bg-primary/5 border-primary/15">
            <CardContent className="p-5 text-center">
              <p className="font-semibold mb-1">Join the conversation</p>
              <p className="text-sm text-muted-foreground mb-3">Login to post, comment, and like</p>
              <Button size="sm" onClick={() => window.location.href = '/login'} data-testid="login-prompt-btn">
                Login to Participate
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
